<template>
	<div>
		<v-card :flat="flat">
			<v-card-title class="forms-list__header">
				<div class="text-xs-left">
					<div class="title">Forms</div>
					<div class="caption text--secondary">{{ formSummaryText }}</div>
				</div>
				<v-spacer></v-spacer>
				<div class="forms-list__header-actions">
					<v-btn color="primary" @click="$emit('createForm')">Create Form</v-btn>
				</div>
			</v-card-title>

			<v-card-text class="forms-list__body">

				<div class="forms-list__filters">
					<v-text-field v-model="search" append-icon="search" label="Search forms" single-line hide-details
						class="forms-list__search mb-0"></v-text-field>
					<v-select v-model="categoryFilter" :items="categoryOptions" class="forms-list__category-filter mb-0"
						label="Category" item-text="text" item-value="value" clearable single-line hide-details
						no-data-text="No categories"></v-select>
					<v-btn v-if="hasActiveFilters" flat small class="forms-list__clear-filters" @click="clearFilters">
						Clear filters
					</v-btn>
				</div>

				<v-tabs v-model="statusTab" class="forms-list__status-tabs">
					<v-tab class="noPrint">Active</v-tab>
					<v-tab class="noPrint">Archived</v-tab>
				</v-tabs>

				<v-data-table disable-initial-sort :headers="headers" :items="visibleForms" :pagination.sync="pagination"
					:rows-per-page-items="rowsPerPageItems" :loading="isLoading" class="forms-list__table">
					<template slot="items" slot-scope="props">
						<tr>
							<td class="text-xs-left forms-list__cell forms-list__cell--name">
								<button v-if="props.item.status === 'Active'" type="button" class="forms-list__name-button"
									@click="$emit('editForm', props.item)">
									{{ props.item.name || 'Untitled form' }}
								</button>
								<span v-else>{{ props.item.name || 'Untitled form' }}</span>
							</td>
							<td class="text-xs-left forms-list__cell forms-list__cell--description">{{ props.item.description || 'No description provided.' }}</td>
							<td class="text-xs-left forms-list__cell forms-list__cell--category">
								<v-chip v-if="props.item.categoryName" small label class="forms-list__category-chip"
									@click.native.stop="applyCategoryFilter(props.item)">
									{{ props.item.categoryName }}
								</v-chip>
								<v-chip v-else small label class="forms-list__category-chip forms-list__category-chip--muted"
									@click.native.stop="applyCategoryFilter(props.item)">
									Uncategorised
								</v-chip>
							</td>
							<td class="text-xs-left forms-list__cell">
								<v-chip v-if="props.item.isSelfAssignable" small color="info" text-color="white">
									Self-assignable
								</v-chip>
								<span v-else class="text--secondary">Manager/admin assigned</span>
							</td>
							<td class="text-xs-left forms-list__cell forms-list__cell--version">v{{ props.item.currentVersionNo }}</td>
							<td class="text-xs-left forms-list__cell forms-list__cell--status">
								<v-chip small :color="getStatusColor(props.item.status)"
									:dark="getStatusColor(props.item.status) !== ''">{{ props.item.status }}</v-chip>
							</td>
							<td class="text-xs-left forms-list__cell forms-list__cell--updated">
								<AppDate :date="props.item.lastUpdatedDateUtc || props.item.createdDateUtc" />
							</td>
							<td class="text-xs-right forms-list__cell forms-list__cell--actions">
								<v-menu offset-y>
									<template v-slot:activator="{ on }">
										<v-btn small icon flat v-on="on">
											<v-icon>mdi-dots-horizontal</v-icon>
										</v-btn>
									</template>
									<v-list>
										<v-list-tile v-for="(action, index) in getAvailableActionsForForm(props.item)"
											:key="index" @click="action.callback">
											<v-list-tile-action>
												<v-icon>{{ action.icon }}</v-icon>
											</v-list-tile-action>
											<v-list-tile-title>{{ action.title }}</v-list-tile-title>
										</v-list-tile>
									</v-list>
								</v-menu>
							</td>
						</tr>
					</template>
					<template slot="no-data">
						<div class="forms-list__empty-state pa-4">
							<v-icon color="grey lighten-1" size="42" class="mb-2">mdi-file-document-outline</v-icon>
							<div class="subheading mb-1">{{ noDataTitle }}</div>
							<div class="text--secondary">{{ noDataText }}</div>
							<div class="forms-list__empty-actions">
								<v-btn v-if="hasActiveFilters" flat small color="primary" @click="clearFilters">
									Clear filters
								</v-btn>
								<v-btn v-else-if="statusTab === 0" color="primary" small @click="$emit('createForm')">
									Create Form
								</v-btn>
							</div>
						</div>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>

	</div>
</template>

<script>
export default {
	name: 'FormsList',
	props: {
		forms: {
			type: Array,
			default: () => []
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		flat: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			statusTab: 0,
			search: '',
			categoryFilter: null,
			pagination: {
				sortBy: 'name',
				descending: false,
				rowsPerPage: 12
			},
			rowsPerPageItems: [12, 30, 60, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
		};
	},
	computed: {
		headers() {
			return [
				{ text: 'Name', value: 'name' },
				{ text: 'Description', value: 'description' },
				{ text: 'Category', value: 'categoryName' },
				{ text: `${this.$term('employee', { case: 'title' })} access`, value: 'isSelfAssignable' },
				{ text: 'Version', value: 'currentVersionNo' },
				{ text: 'Status', value: 'status' },
				{ text: 'Updated', value: 'lastUpdatedDateUtc' },
				{ text: '', value: 'actions', sortable: false, align: 'right' }
			];
		},
		activeForms() {
			return this.forms.filter(form => form && form.status === 'Active');
		},
		archivedForms() {
			return this.forms.filter(form => form && form.status === 'Archived');
		},
		formsForStatusTab() {
			return this.statusTab === 1 ? this.archivedForms : this.activeForms;
		},
		visibleForms() {
			let forms = this.formsForStatusTab;

			if (this.categoryFilter) {
				forms = forms.filter(form => this.getCategoryFilterValue(form) === this.categoryFilter);
			}

			if (this.normalisedSearch) {
				forms = forms.filter(form => this.formMatchesSearch(form));
			}

			return forms;
		},
		categoryOptions() {
			const categoriesById = {};
			let hasUncategorised = false;

			this.formsForStatusTab.forEach(form => {
				if (!form) return;

				const value = this.getCategoryFilterValue(form);
				if (value === '__uncategorised') {
					hasUncategorised = true;
					return;
				}

				if (!categoriesById[value]) {
					categoriesById[value] = {
						text: form.categoryName,
						value
					};
				}
			});

			const options = Object.values(categoriesById)
				.filter(option => !!option.text)
				.sort((a, b) => a.text.localeCompare(b.text));

			if (hasUncategorised) {
				options.unshift({
					text: 'Uncategorised',
					value: '__uncategorised'
				});
			}

			return options;
		},
		hasActiveFilters() {
			return !!this.categoryFilter || !!this.normalisedSearch;
		},
		normalisedSearch() {
			return this.search.trim().toLowerCase();
		},
		statusLabel() {
			return this.statusTab === 1 ? 'archived' : 'active';
		},
		selectedCategoryText() {
			const selected = this.categoryOptions.find(option => option.value === this.categoryFilter);
			return selected ? selected.text : null;
		},
		formSummaryText() {
			const total = this.formsForStatusTab.length;
			const visible = this.visibleForms.length;
			const noun = total === 1 ? 'form' : 'forms';

			if (this.hasActiveFilters) {
				return `Showing ${visible} of ${total} ${this.statusLabel} ${noun}`;
			}

			return `${total} ${this.statusLabel} ${noun}`;
		},
		noDataTitle() {
			if (this.hasActiveFilters) return 'No forms match these filters';
			if (this.statusTab === 1) return 'No archived forms';
			return 'No active forms';
		},
		noDataText() {
			if (this.hasActiveFilters) {
				if (this.selectedCategoryText && this.normalisedSearch) {
					return `No ${this.statusLabel} forms match "${this.search}" in ${this.selectedCategoryText}.`;
				}

				if (this.selectedCategoryText) {
					return `No ${this.statusLabel} forms are in ${this.selectedCategoryText}.`;
				}

				return `No ${this.statusLabel} forms match "${this.search}".`;
			}

			return this.statusTab === 1 ? 'Archived forms will appear here.' : 'Create your first form to get started.';
		}
	},
	watch: {
		statusTab() {
			this.categoryFilter = null;
			this.pagination.page = 1;
		},
		categoryFilter() {
			this.pagination.page = 1;
		},
		search() {
			this.pagination.page = 1;
		}
	},
	methods: {
		getCategoryFilterValue(form) {
			if (!form || !form.categoryName) return '__uncategorised';

			return form.categoryId || form.categoryName;
		},
		applyCategoryFilter(form) {
			this.categoryFilter = this.getCategoryFilterValue(form);
		},
		clearFilters() {
			this.search = '';
			this.categoryFilter = null;
		},
		formMatchesSearch(form) {
			const searchableText = [
				form.name,
				form.description,
				form.categoryName || 'Uncategorised',
				form.isSelfAssignable ? `Self-assignable ${this.$term('employee')} access` : 'Manager/admin assigned',
				form.status,
				form.currentVersionNo ? `v${form.currentVersionNo}` : ''
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase();

			return searchableText.includes(this.normalisedSearch);
		},
		getAvailableActionsForForm(form) {
			if (form.status === 'Archived') {
				return [
					{
						title: 'Unarchive',
						icon: 'undo',
						callback: () => this.$emit('unarchiveForm', form)
					}
				];
			}

			return [
				{
					title: 'Edit',
					icon: 'edit',
					callback: () => this.$emit('editForm', form)
				},
				{
					title: 'Assign',
					icon: 'assignment_ind',
					callback: () => this.$emit('assignForm', form)
				},
				{
					title: 'Archive',
					icon: 'archive',
					callback: () => this.$emit('archiveForm', form)
				}
			];
		},
		getStatusColor(status) {
			switch (status) {
				case 'Active':
					return 'success';
				case 'Archived':
					return 'warning';
				case 'Deleted':
					return 'error';
				default:
					return '';
			}
		}
	}
};
</script>

<style scoped>
.forms-list__header {
	align-items: center;
	padding-bottom: 8px;
}

.forms-list__header-actions {
	display: flex;
	gap: 8px;
	align-items: center;
}

.forms-list__body {
	padding-top: 0;
}

.forms-list__status-tabs {
	margin-top: 2px;
}

.forms-list__filters {
	display: flex;
	gap: 12px;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 10px;
	min-height: 48px;
}

.forms-list__search {
	max-width: 520px;
}

.forms-list__category-filter {
	max-width: 260px;
}

.forms-list__clear-filters {
	flex: 0 0 auto;
}

.forms-list__table {
	margin-top: 8px;
}

.forms-list__cell {
	vertical-align: middle;
}

.forms-list__cell--name {
	min-width: 150px;
}

.forms-list__cell--description {
	white-space: normal;
}

.forms-list__name-button {
	padding: 0;
	border: 0;
	background: transparent;
	color: #3569c7;
	font: inherit;
	cursor: pointer;
	text-align: left;
}

.forms-list__name-button:hover {
	text-decoration: underline;
}

.forms-list__category-chip {
	cursor: pointer;
}

.forms-list__category-chip--muted {
	color: rgba(0, 0, 0, 0.54);
}

.forms-list__empty-state {
	text-align: center;
}

.forms-list__empty-actions {
	margin-top: 8px;
}

@media (max-width: 959px) {
	.forms-list__header {
		align-items: stretch;
		flex-direction: column;
	}

	.forms-list__header-actions,
	.forms-list__filters {
		align-items: stretch;
		flex-direction: column;
	}

	.forms-list__search,
	.forms-list__category-filter {
		max-width: none;
	}
}
</style>
